import { useRef, useContext, useState, FC, ChangeEvent, ReactElement, useEffect } from 'react'
import { Input, useDisclosure } from '@chakra-ui/react'

import UploadImageContext, { IUploadImageContext } from './Context'
import { MainImage, UploadImageProviderProps } from '../types'
import { ImagesFromUploadImage } from '../types'

type Props = UploadImageProviderProps & {
  children: ReactElement
  images?: ImagesFromUploadImage[]
}

const UploadImageProvider: FC<Props> = ({
  children,
  onUpdate,
  onDelete,
  onUpload,
  isLoading,
  defaultImage,
  images = [],
  size = '125px',
  rounded = 'full',
  texts,
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure()

  const [view, setView] = useState('Gallery')
  const [image, setImage] = useState<MainImage>({
    id: defaultImage?.id,
    url: defaultImage?.url,
  })
  const [newImage, setNewImage] = useState<MainImage>({})

  const inputRef = useRef<HTMLInputElement | null>(null)

  const openModal = () => onOpen()

  const closeModal = () => {
    setView('Gallery')
    onClose()
  }

  const handleOnClick = () => {
    if (inputRef.current) inputRef.current.value = ''
    inputRef.current?.click()
    openModal()
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!defaultImage) openModal()

    if (!event.target.files) return

    const file = event.target.files[0]
    const url = URL.createObjectURL(file)

    setNewImage({ file, url })
    setView('Cropper')
  }

  useEffect(() => {
    if (defaultImage) setImage(defaultImage)
  }, [defaultImage])

  return (
    <UploadImageContext.Provider
      value={{
        texts,
        isOpen,
        isLoading,
        images,
        image,
        newImage,
        size,
        rounded,
        view,
        openModal,
        closeModal,
        setImage,
        setNewImage,
        setView,
        onUpdate,
        onDelete,
        onUpload,
        handleOnClick,
        handleOnChange,
      }}
    >
      <>
        <Input ref={inputRef} onChange={handleOnChange} type="file" accept="images/*" hidden />
        {children}
      </>
    </UploadImageContext.Provider>
  )
}

export default UploadImageProvider

export const useUploadImage = () => useContext<IUploadImageContext>(UploadImageContext)
